import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { RecordLink } from '../components/RecordLink';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Users, Clock, AlertTriangle, CheckCircle, Timer, TrendingUp, CalendarClock, UserCog, ChevronRight
} from 'lucide-react';

export default function HumanResources() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/hr/staff');
      setStaff(res.data);
    } catch (e) {
      console.error('Failed to load HR data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  const handleToggleClockIn = async (userId, currentValue) => {
    try {
      await api.patch(`/hr/staff/${userId}`, { require_clock_in: !currentValue });
      toast.success(`Clock-in requirement ${!currentValue ? 'enabled' : 'disabled'}`);
      loadStaff();
    } catch (e) {
      toast.error('Failed to update setting');
    }
  };

  const now = new Date();
  const monthName = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const weekLabel = 'This Week';

  return (
    <div className="p-4 md:p-6 max-w-full" data-testid="human-resources-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Human Resources</h1>
          <p className="text-sm text-gray-500 mt-1">Staff clock-in settings and hours summary</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard
          icon={<Users className="h-5 w-5 text-blue-600" />}
          label="Total Staff"
          value={staff.length}
          testId="total-staff-card"
        />
        <SummaryCard
          icon={<CalendarClock className="h-5 w-5 text-green-600" />}
          label="Clock-In Required"
          value={staff.filter(s => s.require_clock_in !== false).length}
          testId="clock-in-required-card"
        />
        <SummaryCard
          icon={<Timer className="h-5 w-5 text-amber-600" />}
          label="Overtime (Month)"
          value={`${staff.reduce((sum, s) => sum + (s.hr_summary?.month_overtime_minutes || 0), 0)}m`}
          testId="overtime-month-card"
        />
        <SummaryCard
          icon={<AlertTriangle className="h-5 w-5 text-red-500" />}
          label="Late (Month)"
          value={staff.reduce((sum, s) => sum + (s.hr_summary?.month_late_count || 0), 0)}
          testId="late-month-card"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading staff data...</div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block">
            <Card>
              <CardContent className="p-0">
                <Table data-testid="hr-staff-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Require Clock-In</TableHead>
                      <TableHead className="text-center">Today</TableHead>
                      <TableHead className="text-right">{weekLabel} Hours</TableHead>
                      <TableHead className="text-right">{monthName} Hours</TableHead>
                      <TableHead className="text-right">Overtime</TableHead>
                      <TableHead className="text-right">Late</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staff.map(member => (
                      <TableRow key={member.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate(`/human-resources/${member.id}`)} data-testid={`hr-row-${member.id}`}>
                        <TableCell>
                          <RecordLink
                            to={member.id ? `/human-resources/${member.id}` : null}
                            className="font-medium text-blue-600 hover:underline"
                            fallbackClassName="font-medium"
                            data-testid={`hr-name-link-${member.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {member.full_name}
                          </RecordLink>
                          <div className="text-xs text-gray-500">{member.email}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={member.role === 'admin' ? 'default' : 'secondary'} className="text-xs capitalize">
                            {member.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={member.is_active ? 'outline' : 'destructive'} className="text-xs">
                            {member.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                          <Switch
                            checked={member.require_clock_in !== false}
                            onCheckedChange={() => handleToggleClockIn(member.id, member.require_clock_in !== false)}
                            data-testid={`toggle-clock-in-${member.id}`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <TodayStatusBadge status={member.hr_summary?.today_status} requireClockIn={member.require_clock_in !== false} />
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {member.hr_summary?.week_hours || 0}h
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {member.hr_summary?.month_hours || 0}h
                        </TableCell>
                        <TableCell className="text-right">
                          {member.hr_summary?.month_overtime_minutes > 0 ? (
                            <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
                              +{member.hr_summary.month_overtime_minutes}m
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {member.hr_summary?.month_late_count > 0 ? (
                            <Badge variant="destructive" className="text-xs">
                              {member.hr_summary.month_late_count}
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-400">0</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3" data-testid="hr-mobile-list">
            {staff.map(member => (
              <Card key={member.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/human-resources/${member.id}`)} data-testid={`hr-card-${member.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <RecordLink
                        to={member.id ? `/human-resources/${member.id}` : null}
                        className="font-semibold text-sm text-blue-600 hover:underline"
                        fallbackClassName="font-semibold text-sm"
                        data-testid={`hr-card-name-link-${member.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {member.full_name}
                      </RecordLink>
                      <div className="text-xs text-gray-500">{member.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={member.role === 'admin' ? 'default' : 'secondary'} className="text-xs capitalize">
                        {member.role}
                      </Badge>
                      <TodayStatusBadge status={member.hr_summary?.today_status} requireClockIn={member.require_clock_in !== false} />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between py-2 border-t" onClick={e => e.stopPropagation()}>
                    <span className="text-sm text-gray-600">Require Clock-In</span>
                    <Switch
                      checked={member.require_clock_in !== false}
                      onCheckedChange={() => handleToggleClockIn(member.id, member.require_clock_in !== false)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                    <div className="bg-gray-50 rounded p-2">
                      <div className="text-gray-500">Week Hours</div>
                      <div className="font-semibold text-base">{member.hr_summary?.week_hours || 0}h</div>
                    </div>
                    <div className="bg-gray-50 rounded p-2">
                      <div className="text-gray-500">Month Hours</div>
                      <div className="font-semibold text-base">{member.hr_summary?.month_hours || 0}h</div>
                    </div>
                    {member.hr_summary?.month_overtime_minutes > 0 && (
                      <div className="bg-amber-50 rounded p-2">
                        <div className="text-amber-600">Overtime</div>
                        <div className="font-semibold text-base text-amber-700">+{member.hr_summary.month_overtime_minutes}m</div>
                      </div>
                    )}
                    {member.hr_summary?.month_late_count > 0 && (
                      <div className="bg-red-50 rounded p-2">
                        <div className="text-red-500">Late Count</div>
                        <div className="font-semibold text-base text-red-600">{member.hr_summary.month_late_count}</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-gray-50">{icon}</div>
        <div>
          <div className="text-xs text-gray-500">{label}</div>
          <div className="text-lg font-bold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function TodayStatusBadge({ status, requireClockIn }) {
  if (!requireClockIn) return <span className="text-xs text-gray-400">N/A</span>;
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  switch (status) {
    case 'clocked_in':
      return <Badge variant="default" className="text-xs"><CheckCircle className="h-3 w-3 mr-0.5" />In</Badge>;
    case 'completed':
      return <Badge variant="outline" className="text-xs">Done</Badge>;
    case 'scheduled':
      return <Badge variant="secondary" className="text-xs"><Clock className="h-3 w-3 mr-0.5" />Waiting</Badge>;
    case 'missed':
      return <Badge variant="destructive" className="text-xs">Missed</Badge>;
    default:
      return <span className="text-xs text-gray-400">{status}</span>;
  }
}
